import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import logo from "../../assets/logo.png";

const links = [
  { label: "Services", href: "#services" },
  { label: "Results", href: "#results" },
  { label: "Reviews", href: "#reviews" },
  { label: "Blog", href: "#blog" },
  { label: "Why Us", href: "#trust" },
  { label: "Contact", href: "#estimate" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-3">
          <img src={logo} alt="First Response Exterior Cleaning LLC" className="h-11 sm:h-14 w-auto" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-display font-bold text-sm tracking-tight text-white">FIRST RESPONSE</span>
            <span className="fr-mono text-[9px] tracking-[0.3em] text-fr-blue-light">EXTERIOR CLEANING LLC</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className="fr-mono text-xs tracking-[0.15em] uppercase text-white/70 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            href="tel:8432975559"
            data-testid="header-call-btn"
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors fr-mono text-sm"
          >
            <Phone size={15} className="text-fr-blue-light" /> 843-297-5559
          </a>
          <a href="#estimate" data-testid="header-estimate-btn" className="btn-primary text-sm">
            Free Estimate
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(" ", "-")}`}
                  className="fr-mono text-sm tracking-widest uppercase text-white/80"
                >
                  {l.label}
                </a>
              ))}
              <a href="tel:8432975559" className="btn-outline text-center text-sm">Call 843-297-5559</a>
              <a href="#estimate" onClick={() => setOpen(false)} className="btn-primary text-center text-sm">Free Estimate</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
